
import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { type BackupResult } from "@/utils/wordpressApi";
import BackupStatusBadge from "./BackupStatusBadge";
import BackupActionButtons from "./BackupActionButtons";
import BackupSecurityStatus from "./BackupSecurityStatus";
import { Progress } from "@/components/ui/progress";
import { getBackupSecurityStatus } from "@/services/securityService";

interface BackupTableProps {
  backups: BackupResult[];
  websiteId: string;
  deleteInProgress: string | null;
  onDeleteBackup: (backupId: string) => void;
  formatDate: (dateString: string) => string;
}

const BackupTable: React.FC<BackupTableProps> = ({
  backups,
  websiteId,
  deleteInProgress,
  onDeleteBackup,
  formatDate,
}) => {
  // Store security statuses for each backup
  const [securityStatuses, setSecurityStatuses] = useState<Record<string, {
    isScanned: boolean;
    isClean: boolean;
    threatDetails?: string;
    scanDate?: string;
  }>>({});
  
  // Fetch security status for each backup
  useEffect(() => {
    const fetchSecurityStatuses = async () => {
      const statuses: Record<string, any> = {};
      
      for (const backup of backups) {
        if (backup.status === 'completed') {
          const status = await getBackupSecurityStatus(backup.id);
          statuses[backup.id] = status;
        }
      }
      
      setSecurityStatuses(statuses);
    };
    
    fetchSecurityStatuses();
  }, [backups]);

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Date</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Size</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Security</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {backups.map((backup) => (
          <TableRow key={backup.id}>
            <TableCell className="font-medium">
              {formatDate(backup.createdAt)}
            </TableCell>
            <TableCell>
              <Badge variant={backup.backupType === 'full' ? 'default' : 'secondary'}>
                {backup.backupType === 'full' ? 'Full' : 'Incremental'}
              </Badge>
            </TableCell>
            <TableCell>
              {backup.status === "in-progress" ? (
                <div className="space-y-2">
                  <div className="text-sm font-medium">
                    {backup.size || "Calculating..."}
                  </div>
                  <Progress 
                    value={backup.progress || 0} 
                    className="h-2 w-full" 
                    aria-label={`Backup progress: ${backup.progress || 0}%`}
                  />
                  <div className="text-xs text-muted-foreground">
                    {backup.progress || 0}% complete
                  </div>
                </div>
              ) : (
                <div className="text-sm">
                  {backup.size || "Unknown"}
                </div>
              )}
            </TableCell>
            <TableCell>
              <BackupStatusBadge 
                status={backup.status} 
                progress={backup.progress}
              />
            </TableCell>
            <TableCell>
              {backup.status === "completed" ? (
                <BackupSecurityStatus
                  isScanned={securityStatuses[backup.id]?.isScanned || false}
                  isClean={securityStatuses[backup.id]?.isClean}
                  threatDetails={securityStatuses[backup.id]?.threatDetails}
                  scanDate={securityStatuses[backup.id]?.scanDate}
                />
              ) : (
                <span className="text-xs text-gray-400">
                  {backup.status === "in-progress" ? "Backup in progress..." : "Pending completion"}
                </span>
              )}
            </TableCell>
            <TableCell className="text-right">
              <BackupActionButtons
                backupId={backup.id}
                websiteId={websiteId || ""}
                status={backup.status}
                downloadUrl={backup.downloadUrl || ""}
                isDeleting={deleteInProgress === backup.id}
                onDelete={onDeleteBackup}
                backupType={backup.backupType}
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default BackupTable;
